import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startStressPolling, stopStressPolling, updateStressData, setStressPollingError } from '../actions';
import { fetchStressData } from '../api/stressCheckApi';
/**
 * Custom hook for managing stress data polling
 */
export function useStressPolling() {
    const dispatch = useDispatch();
    const intervalRef = useRef(null);
    const { visible, polling, pollingInterval } = useSelector((state) => ({
        visible: state['features/stress-check']?.visible || false,
        polling: state['features/stress-check']?.polling || false,
        pollingInterval: state['features/stress-check']?.pollingInterval || 1000
    }));
    /**
     * Fetches stress data and updates Redux state
     */
    const fetchAndUpdateStressData = async () => {
        try {
            const stressData = await fetchStressData();
            dispatch(updateStressData(stressData));
        }
        catch (error) {
            console.error('Failed to fetch stress data:', error);
            dispatch(setStressPollingError(error instanceof Error ? error.message : 'Unknown error'));
        }
    };
    /**
     * Starts the polling interval
     */
    const startPolling = () => {
        if (intervalRef.current) {
            return; // Already polling
        }
        console.log('Starting stress data polling...');
        dispatch(startStressPolling());
        // Fetch immediately
        fetchAndUpdateStressData();
        // Set up interval
        intervalRef.current = setInterval(() => {
            fetchAndUpdateStressData();
        }, pollingInterval);
    };
    /**
     * Stops the polling interval
     */
    const stopPolling = () => {
        if (intervalRef.current) {
            console.log('Stopping stress data polling...');
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            dispatch(stopStressPolling());
        }
    };
    // Effect to manage polling based on panel visibility
    useEffect(() => {
        if (visible) {
            startPolling();
        }
        else {
            stopPolling();
        }
        // Cleanup on unmount
        return () => {
            stopPolling();
        };
    }, [visible, pollingInterval]);
    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);
    return {
        isPolling: polling,
        startPolling,
        stopPolling
    };
}
